<template>
  <div
      v-if="showModal"
      class="modalOverlay tw-fixed tw-w-full tw-h-full tw-top-0 tw-left-0 tw-flex tw-justify-center tw-items-center tw-z-[9999]"
  >
    <div
        class="tw-text-2xl tw-bg-white tw-p-6 tw-flex tw-flex-col tw-gap-4 tw-rounded-[40px] tw-text-center tw-w-[1000px] tw-max-h-[90vh]"
    >
      <span
          class="tw-text-2xl tw-font-semibold tw-flex tw-justify-between tw-items-center tw-px-2"
      >
        <span>Choose which elements u want in image</span>
        <span
            @click="close(false)"
            class="tw-bg-bgLight tw-rounded-full tw-p-2 tw-font-thin tw-text-sm tw-w-4 tw-h-4 tw-flex tw-items-center tw-justify-center tw-cursor-pointer hover:tw-bg-bgLightHover tw-transition-colors"
            style="padding: 0.75rem !important"
        >&#x2715;</span
        >
      </span>

      <div class="layers-section tw-flex tw-justify-around tw-gap-4 tw-mx-4 tw-overflow-hidden">
        <div class="side-container tw-flex-1 tw-flex tw-flex-col tw-gap-3">
          <div class="canvas-preview-wrapper tw-bg-bgLightMuted tw-rounded-2xl tw-p-3 tw-flex tw-items-center tw-justify-center"
               style="min-height: 200px;">
            <img v-if="frontCanvasPreview" :src="frontCanvasPreview"
                 class="tw-max-w-full tw-max-h-[180px] tw-rounded-lg tw-shadow-md"/>
            <div v-else class="tw-text-sm tw-text-gray-500">Front Side Preview</div>
          </div>

          <div class="objectsContainer tw-flex-1 tw-rounded-2xl tw-overflow-hidden tw-flex tw-flex-col">
            <h5 class="tw-py-3 tw-text-base tw-font-semibold tw-bg-bgLightMuted tw-sticky tw-top-0 tw-z-10">Front Layers</h5>
            <div class="layers-list tw-overflow-y-auto tw-flex-1 tw-px-2 tw-py-2">
              <div
                  v-for="(obj, index) in frontObjects"
                  :key="index"
                  class="layer-item tw-flex tw-items-center tw-justify-between tw-p-2 tw-mb-1.5 tw-bg-white tw-rounded-lg hover:tw-bg-gray-50 tw-transition-colors tw-border tw-border-gray-200"
              >
                <div class="tw-flex tw-items-center tw-gap-2 tw-flex-1 tw-min-w-0">
                  <div class="layer-preview tw-flex-shrink-0">
                    <img
                        v-if="obj.src"
                        :src="obj.src"
                        class="tw-rounded-md object-contain tw-border tw-border-gray-300"
                        style="max-width: 60px; max-height: 40px;"
                    />
                    <div v-else
                         class="unknown-preview tw-bg-gradient-to-br tw-from-gray-100 tw-to-gray-200 tw-rounded-md tw-flex tw-items-center tw-justify-center">
                      <svg class="tw-w-4 tw-h-4 tw-text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                      </svg>
                    </div>
                  </div>
                  <div class="layer-info tw-flex-1 tw-min-w-0 tw-text-left">
                    <div class="layer-type tw-text-[10px] tw-text-gray-500 tw-uppercase tw-font-medium">
                      {{ obj.isText ? 'Text' : (obj.type === 'image' ? 'Image' : obj.type) }}
                    </div>
                    <div class="layer-content tw-text-xs tw-font-medium tw-text-gray-800 tw-truncate">
                      <span v-if="obj.text" class="tw-font-normal">{{ obj.text }}</span>
                      <span v-else-if="obj.imageName" class="tw-text-gray-600">{{ obj.imageName }}</span>
                      <span v-else class="tw-text-gray-600">{{ obj.type }}</span>
                    </div>
                  </div>
                </div>
                <label class="checkbox-wrapper tw-cursor-pointer tw-flex tw-items-center">
                  <input
                      type="checkbox"
                      v-model="obj.localVisible"
                      @change="updateLayerVisibility(obj.objKey, obj.localVisible)"
                      class="tw-w-4 tw-h-4 tw-cursor-pointer tw-accent-solYellow"
                  />
                </label>
              </div>
            </div>
          </div>
        </div>

        <div class="side-container tw-flex-1 tw-flex tw-flex-col tw-gap-3" v-if="backCanvas">
          <div class="canvas-preview-wrapper tw-bg-bgLightMuted tw-rounded-2xl tw-p-3 tw-flex tw-items-center tw-justify-center"
               style="min-height: 200px;">
            <img v-if="backCanvasPreview" :src="backCanvasPreview"
                 class="tw-max-w-full tw-max-h-[180px] tw-rounded-lg tw-shadow-md"/>
            <div v-else class="tw-text-sm tw-text-gray-500">Back Side Preview</div>
          </div>

          <div class="objectsContainer tw-flex-1 tw-rounded-2xl tw-overflow-hidden tw-flex tw-flex-col">
            <h5 class="tw-py-3 tw-text-base tw-font-semibold tw-bg-bgLightMuted tw-sticky tw-top-0 tw-z-10">Back Layers</h5>
            <div class="layers-list tw-overflow-y-auto tw-flex-1 tw-px-2 tw-py-2">
              <div
                  v-for="(obj, index) in backObjects"
                  :key="index"
                  class="layer-item tw-flex tw-items-center tw-justify-between tw-p-2 tw-mb-1.5 tw-bg-white tw-rounded-lg hover:tw-bg-gray-50 tw-transition-colors tw-border tw-border-gray-200"
              >
                <div class="tw-flex tw-items-center tw-gap-2 tw-flex-1 tw-min-w-0">
                  <div class="layer-preview tw-flex-shrink-0">
                    <img
                        v-if="obj.src"
                        :src="obj.src"
                        class="tw-rounded-md object-contain tw-border tw-border-gray-300"
                        style="max-width: 60px; max-height: 40px;"
                    />
                    <div v-else
                         class="unknown-preview tw-bg-gradient-to-br tw-from-gray-100 tw-to-gray-200 tw-rounded-md tw-flex tw-items-center tw-justify-center">
                      <svg class="tw-w-4 tw-h-4 tw-text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                      </svg>
                    </div>
                  </div>
                  <div class="layer-info tw-flex-1 tw-min-w-0 tw-text-left">
                    <div class="layer-type tw-text-[10px] tw-text-gray-500 tw-uppercase tw-font-medium">
                      {{ obj.isText ? 'Text' : (obj.type === 'image' ? 'Image' : obj.type) }}
                    </div>
                    <div class="layer-content tw-text-xs tw-font-medium tw-text-gray-800 tw-truncate">
                      <span v-if="obj.text" class="tw-font-normal">{{ obj.text }}</span>
                      <span v-else-if="obj.imageName" class="tw-text-gray-600">{{ obj.imageName }}</span>
                      <span v-else class="tw-text-gray-600">{{ obj.type }}</span>
                    </div>
                  </div>
                </div>
                <label class="checkbox-wrapper tw-cursor-pointer tw-flex tw-items-center">
                  <input
                      type="checkbox"
                      v-model="obj.localVisible"
                      @change="updateLayerVisibility(obj.objKey, obj.localVisible)"
                      class="tw-w-4 tw-h-4 tw-cursor-pointer tw-accent-solYellow"
                  />
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="options-section tw-text-left tw-text-sm tw-px-6 tw-py-4 tw-bg-gray-50 tw-rounded-2xl tw-mx-4">
        <div class="tw-flex tw-items-center tw-gap-3 tw-mb-3">
          <input
              id="transparentCanvas"
              type="checkbox"
              v-model="transparentCanvas"
              class="tw-w-5 tw-h-5 tw-cursor-pointer tw-accent-solYellow"
          />
          <label for="transparentCanvas" class="tw-cursor-pointer tw-font-medium">Transparent Canvas</label>
        </div>
        <div class="tw-flex tw-items-center tw-gap-3 tw-mb-3">
          <input
              type="checkbox"
              id="replaceCheckbox"
              v-model="replacePixelsColor"
              class="tw-w-5 tw-h-5 tw-cursor-pointer tw-accent-solYellow"
          />
          <label for="replaceCheckbox" class="tw-cursor-pointer tw-font-medium">Replace pixels color</label>
        </div>
        <template v-if="replacePixelsColor">
          <div class="tw-flex tw-items-center tw-gap-4 tw-mt-4 tw-ml-8">
            <div class="tw-flex tw-items-center tw-gap-2">
              <label class="tw-text-xs tw-font-medium tw-text-gray-600">From:</label>
              <input
                  type="color"
                  v-model="fromPixelColor"
                  class="tw-w-12 tw-h-8 tw-rounded tw-cursor-pointer tw-border tw-border-gray-300"
              />
              <span class="tw-text-xs tw-font-mono">{{ fromPixelColor }}</span>
            </div>
            <span class="tw-text-gray-400">→</span>
            <div class="tw-flex tw-items-center tw-gap-2">
              <label class="tw-text-xs tw-font-medium tw-text-gray-600">To:</label>
              <input
                  type="color"
                  v-model="toPixelColor"
                  class="tw-w-12 tw-h-8 tw-rounded tw-cursor-pointer tw-border tw-border-gray-300"
              />
              <span class="tw-text-xs tw-font-mono">{{ toPixelColor }}</span>
            </div>
          </div>
        </template>
      </div>

      <div class="tw-flex tw-gap-4 tw-px-4">
        <button
            class="tw-bg-solYellow hover:tw-bg-solYellowHover tw-border-none tw-text-textDark tw-font-semibold tw-rounded-l-3xl tw-rounded-r-3xl tw-text-lg tw-flex-1 tw-p-2 tw-py-4 tw-cursor-pointer tw-flex tw-justify-center tw-items-center tw-transition-colors tw-shadow-sm"
            @click="close(true, 'png')"
            style="color: var(--color-text-dark) !important"
        >
          <svg class="tw-w-5 tw-h-5 tw-mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
          </svg>
          Download PNG
        </button>
        <button
            class="tw-bg-solYellow hover:tw-bg-solYellowHover tw-border-none tw-text-textDark tw-font-semibold tw-rounded-l-3xl tw-rounded-r-3xl tw-text-lg tw-flex-1 tw-p-2 tw-py-4 tw-cursor-pointer tw-flex tw-justify-center tw-items-center tw-transition-colors tw-shadow-sm"
            @click="close(true, 'pdf')"
            style="color: var(--color-text-dark) !important"
        >
          <svg class="tw-w-5 tw-h-5 tw-mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/>
          </svg>
          Download PDF
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { trackEvent } from '@/utils/tracking';

export default {
  data() {
    return {
      showModal: false,
      fromPixelColor: "#fff",
      toPixelColor: "#fffffc",
      transparentCanvas: false,
      replacePixelsColor: false,
      frontCanvasPreview: null,
      backCanvasPreview: null,
      updateKey: 0,
      layerVisibilityStates: {},
    };
  },
  computed: {
    backCanvas() {
      return window.backCanvas;
    },
    frontObjects() {
      // Force recalculation when updateKey changes
      this.updateKey;

      if (!window.frontCanvas) return [];

      return window.frontCanvas.getObjects().map((obj, index) => {
        const isTextType = obj.type === 'text' || obj.type === 'i-text' || obj.type === 'textbox';
        const isImageType = obj.type === 'image';

        // Extract image name from various possible sources
        let imageName = null;
        if (isImageType) {
          if (obj.name) {
            imageName = obj.name;
          } else if (obj._originalElement && obj._originalElement.currentSrc) {
            // Extract filename from URL (skip if base64)
            const url = obj._originalElement.currentSrc;
            if (!url.startsWith('data:')) {
              imageName = url.split('/').pop().split('?')[0];
            }
          } else if (obj.src) {
            // Extract filename from URL (skip if base64)
            const url = obj.src;
            if (!url.startsWith('data:')) {
              imageName = url.split('/').pop().split('?')[0];
            }
          }

          // If still no name, use generic name
          if (!imageName) {
            imageName = 'Uploaded Image';
          }
        }

        const objKey = `front-${index}`;
        
        return {
          isText: isTextType,
          text: isTextType ? obj.text : null,
          src: obj.toDataURL ? obj.toDataURL() : '',
          type: obj.type,
          imageName: imageName,
          id: obj.id,
          obj,
          objKey,
          localVisible: this.layerVisibilityStates[objKey] !== undefined ? this.layerVisibilityStates[objKey] : true
        };
      });
    },
    backObjects() {
      // Force recalculation when updateKey changes
      this.updateKey;

      if (!window.backCanvas) return [];

      return window.backCanvas.getObjects().map((obj, index) => {
        const isTextType = obj.type === 'text' || obj.type === 'i-text' || obj.type === 'textbox';
        const isImageType = obj.type === 'image';

        // Extract image name from various possible sources
        let imageName = null;
        if (isImageType) {
          if (obj.name) {
            imageName = obj.name;
          } else if (obj._originalElement && obj._originalElement.currentSrc) {
            // Extract filename from URL (skip if base64)
            const url = obj._originalElement.currentSrc;
            if (!url.startsWith('data:')) {
              imageName = url.split('/').pop().split('?')[0];
            }
          } else if (obj.src) {
            // Extract filename from URL (skip if base64)
            const url = obj.src;
            if (!url.startsWith('data:')) {
              imageName = url.split('/').pop().split('?')[0];
            }
          }

          // If still no name, use generic name
          if (!imageName) {
            imageName = 'Uploaded Image';
          }
        }

        const objKey = `back-${index}`;
        
        return {
          isText: isTextType,
          text: isTextType ? obj.text : null,
          src: obj.toDataURL ? obj.toDataURL() : '',
          type: obj.type,
          imageName: imageName,
          id: obj.id,
          obj,
          objKey,
          localVisible: this.layerVisibilityStates[objKey] !== undefined ? this.layerVisibilityStates[objKey] : true
        };
      });
    },
  },
  methods: {
    updateLayerVisibility(objKey, visible) {
      this.layerVisibilityStates[objKey] = visible;
      this.updateKey++;
      this.$nextTick(() => {
        this.generateCanvasPreviews();
      });
    },
    
    open() {
      this.showModal = true;
      
      // Initialize layer visibility states
      this.layerVisibilityStates = {};
      
      // Initialize front canvas objects
      if (window.frontCanvas) {
        window.frontCanvas.getObjects().forEach((obj, index) => {
          const objKey = `front-${index}`;
          // Set chip to deselected by default
          if (obj === window.chipElement) {
            this.layerVisibilityStates[objKey] = false;
          } else {
            this.layerVisibilityStates[objKey] = true;
          }
        });
      }
      
      // Initialize back canvas objects
      if (window.backCanvas) {
        window.backCanvas.getObjects().forEach((obj, index) => {
          const objKey = `back-${index}`;
          this.layerVisibilityStates[objKey] = true;
        });
      }
      
      this.updateKey++; // Force recomputation of objects
      this.$nextTick(() => {
        this.generateCanvasPreviews();
      });
    },
    generateCanvasPreviews() {
      // Temporarily apply layer visibility states for preview
      const originalStates = {};
      
      if (window.frontCanvas) {
        window.frontCanvas.getObjects().forEach((obj, index) => {
          const objKey = `front-${index}`;
          originalStates[objKey] = obj.visible;
          if (this.layerVisibilityStates[objKey] !== undefined) {
            obj.visible = this.layerVisibilityStates[objKey];
          }
        });
        
        window.frontCanvas.renderAll();
        this.frontCanvasPreview = window.frontCanvas.toDataURL({
          format: 'png',
          quality: 1,
          multiplier: 1
        });
        
        // Restore original states
        window.frontCanvas.getObjects().forEach((obj, index) => {
          const objKey = `front-${index}`;
          obj.visible = originalStates[objKey];
        });
        window.frontCanvas.renderAll();
      }
      
      if (window.backCanvas) {
        window.backCanvas.getObjects().forEach((obj, index) => {
          const objKey = `back-${index}`;
          originalStates[objKey] = obj.visible;
          if (this.layerVisibilityStates[objKey] !== undefined) {
            obj.visible = this.layerVisibilityStates[objKey];
          }
        });
        
        window.backCanvas.renderAll();
        this.backCanvasPreview = window.backCanvas.toDataURL({
          format: 'png',
          quality: 1,
          multiplier: 1
        });
        
        // Restore original states
        window.backCanvas.getObjects().forEach((obj, index) => {
          const objKey = `back-${index}`;
          obj.visible = originalStates[objKey];
        });
        window.backCanvas.renderAll();
      }
    },
    closeModal() {
      trackEvent({
        eventName: "Customizer",
        eventType: "Click",
        action: "CloseModal",
        details: { modal: "DownloadPngModal" },
      });
      this.close(false);
    },
    close(success, format = 'png') {
      this.showModal = false;
      
      if (success) {
        let config = {
          transparentCanvas: this.transparentCanvas,
          format: format,
          layerVisibilityStates: { ...this.layerVisibilityStates }
        };
        if (this.replacePixelsColor) {
          config = {
            ...config,
            from: this.fromPixelColor,
            to: this.toPixelColor,
          };
        }

        if (format === 'pdf') {
          this.$emit("onDownloadPdf", config);
        } else {
          this.$emit("onDownloadPng", config);
        }
      }
      
      // Clean up layer visibility states
      this.layerVisibilityStates = {};
    },
  },
};
</script>

<style lang="scss" scoped>
#cardDesigner {
  .modalOverlay {
    background-color: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
  }

  .layers-section {
    max-height: 500px;
  }

  .side-container {
    display: flex;
    flex-direction: column;
  }

  .canvas-preview-wrapper {
    flex-shrink: 0;
  }

  .objectsContainer {
    background: var(--color-bg-light);
    max-height: 100%;

    .layers-list {
      max-height: 250px;

      &::-webkit-scrollbar {
        width: 8px;
      }

      &::-webkit-scrollbar-track {
        background: var(--color-bg-light-lighter);
        border-radius: 10px;
      }

      &::-webkit-scrollbar-thumb {
        background: var(--color-bg-light-border);
        border-radius: 10px;

        &:hover {
          background: var(--color-bg-light-border-2);
        }
      }
    }
  }

  .layer-preview {
    min-width: 60px;
    max-width: 60px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      max-width: 60px;
      max-height: 40px;
      width: auto;
      height: auto;
    }

    .text-preview,
    .unknown-preview {
      width: 40px;
      height: 40px;
    }
  }
}
</style>
